import { getCurrentMonthAndYear } from '../date';
import { formatAsBRLWithSignal } from '../number';
import { getSeasonalEventPrefix } from '../seasonalEvents';
import { capitalizeEveryWord } from '../string';

const MAX_PERCENTAGE_DISCOUNT = 100;

const parseCurrencyValue = (currencyString) => {
  return parseFloat(currencyString.replace(/[^\d,]/g, '').replace(',', '.'));
};

const formatDiscountValue = (discountValue) => {
  if (!discountValue) {
    return null;
  }

  if (discountValue.includes('%')) {
    const percentageMatch = discountValue.match(/(\d+)%/);

    if (percentageMatch) {
      const percentage = parseInt(percentageMatch[1], 10);

      if (percentage > MAX_PERCENTAGE_DISCOUNT) {
        return null;
      }

      return `${percentage}%`;
    }
  }

  if (discountValue.includes('R$')) {
    const currencyMatch = discountValue.match(/R\$\s*([\d,]+)/);

    if (currencyMatch) {
      const numericValue = parseCurrencyValue(currencyMatch[1]);

      if (!isNaN(numericValue)) {
        return formatAsBRLWithSignal(numericValue);
      }
    }
  }

  return null;
};

const getMaxPercentageDiscount = (activeCoupons) => {
  if (!activeCoupons?.length) {
    return null;
  }

  const percentageDiscounts = activeCoupons
    .map((coupon) => formatDiscountValue(coupon.couponDiscountValue))
    .filter((discount) => discount && discount.includes('%'));

  if (percentageDiscounts.length === 0) {
    return null;
  }

  const maxPercentage = percentageDiscounts.reduce((max, discount) => {
    const percentage = parseInt(discount, 10);
    return !isNaN(percentage) && percentage > max ? percentage : max;
  }, 0);

  return maxPercentage || null;
};

const getMaxCurrencyDiscount = (activeCoupons) => {
  if (!activeCoupons?.length) {
    return null;
  }

  const currencyDiscounts = activeCoupons
    .map((coupon) => formatDiscountValue(coupon.couponDiscountValue))
    .filter((discount) => discount && !discount.includes('%'));

  if (currencyDiscounts.length === 0) {
    return null;
  }

  const maxDiscount = currencyDiscounts.reduce((max, discount) => {
    const numericValue = parseCurrencyValue(discount);

    return !isNaN(numericValue) && numericValue > max ? numericValue : max;
  }, 0);

  return maxDiscount || null;
};

const buildHomeCouponTitle = ({ activeCoupons }) => {
  const { month, year } = getCurrentMonthAndYear();
  const maxPercentage = getMaxPercentageDiscount(activeCoupons);

  const baseTitle = `Melhores cupons de ${month} ${year}`;
  const discountSuffix = maxPercentage
    ? `. Descontos de até ${maxPercentage}%!`
    : '. Os melhores descontos disponíveis!';

  return `${baseTitle}${discountSuffix}`;
};

const buildHomeCouponDescription = ({ activeCoupons, featuredStores }) => {
  const maxPercentage = getMaxPercentageDiscount(activeCoupons);
  const storesLimit = 2;
  const storeNames = featuredStores
    ?.slice(0, storesLimit)
    .map((store) => store.storeName);

  if (!storeNames?.length) {
    const discountText = maxPercentage
      ? `até ${maxPercentage}% OFF`
      : 'exclusivos';

    return `Confira cupons das melhores lojas. Descontos ${discountText}, testados pela comunidade. Atualizado hoje!`;
  }

  const storesText = storeNames.join(' e ');
  const discountText = maxPercentage
    ? `até ${maxPercentage}% OFF`
    : 'exclusivos';

  return `Confira cupons de lojas como ${storesText}. Descontos ${discountText}, testados pela comunidade. Atualizado hoje!`;
};

const buildStoreCouponTitle = ({ activeCoupons, seasonalEvent, storeName }) => {
  const maxPercentage = getMaxPercentageDiscount(activeCoupons);
  const { month, year } = getCurrentMonthAndYear();

  const baseTitle = seasonalEvent
    ? `${seasonalEvent}: Cupom ${storeName}`
    : `Cupom ${storeName} ${capitalizeEveryWord(month)} ${year}`;

  const discountSuffix = maxPercentage
    ? `. Descontos de até ${maxPercentage}% no Promobit!`
    : `. Os melhores descontos no Promobit!`;

  return `${baseTitle}${discountSuffix}`;
};

const buildStoreCouponDescription = ({
  activeCoupons,
  seasonalEvent,
  storeName,
}) => {
  const maxDiscount = getMaxCurrencyDiscount(activeCoupons);

  const baseDescription = `Encontre os melhores cupons da ${storeName}`;
  const seasonalSuffix = seasonalEvent ? ` na ${seasonalEvent}` : '';
  const discountSuffix = maxDiscount
    ? `. Economize até ${formatAsBRLWithSignal(maxDiscount)} com descontos exclusivos`
    : `. Economize com descontos exclusivos`;
  const finalSuffix = '. Testado e aprovado pela comunidade Promobit!';

  return `${baseDescription}${seasonalSuffix}${discountSuffix}${finalSuffix}`;
};

export const getHomeCouponMeta = ({ activeCoupons, featuredStores }) => ({
  title: buildHomeCouponTitle({ activeCoupons }),
  description: buildHomeCouponDescription({ activeCoupons, featuredStores }),
});

export const getStoreCouponsMeta = ({ activeCoupons, storeName }) => {
  const seasonalEvent = getSeasonalEventPrefix();

  return {
    title: buildStoreCouponTitle({
      activeCoupons,
      seasonalEvent,
      storeName,
    }),
    description: buildStoreCouponDescription({
      activeCoupons,
      seasonalEvent,
      storeName,
    }),
  };
};
